import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import { Link } from "react-router-dom";

const API =
  process.env.REACT_APP_API_URL ||
  "https://api.wirelesspress.org";

function formatDate(value) {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatLabel(value) {
  if (!value) {
    return "Unknown";
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getHealthStatus(feed) {
  if (!feed.enabled) {
    return "disabled";
  }

  if (feed.last_error) {
    return "error";
  }

  if (!feed.last_success_at) {
    return "never_synced";
  }

  return feed.health_status || "healthy";
}

function ThreatFeedsPage() {
  const [feeds, setFeeds] =
    useState([]);

  const [selectedFeed, setSelectedFeed] =
    useState(null);

  const [history, setHistory] =
    useState([]);

  const [logs, setLogs] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [actionKey, setActionKey] =
    useState("");

  const [error, setError] =
    useState("");

  const loadFeeds = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await apiFetch(`/threat-intel/feeds`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.detail ||
              "Unable to load threat feeds."
          );
        }

        setFeeds(
          Array.isArray(data)
            ? data
            : data.feeds || []
        );
      } catch (loadError) {
        console.error(
          "Threat feed load error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load threat feeds."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadFeeds();

    const timer = window.setInterval(
      () => loadFeeds(true),
      30000
    );

    return () =>
      window.clearInterval(timer);
  }, [loadFeeds]);

  const openFeed = async (feedId) => {
    setActionKey(`details-${feedId}`);
    setError("");

    try {
      const [
        detailResponse,
        historyResponse,
        logsResponse,
      ] = await Promise.all([
        apiFetch(`/threat-intel/feeds/${feedId}`
        ),
        apiFetch(`/threat-intel/feeds/${feedId}/history?limit=25`
        ),
        apiFetch(`/threat-intel/feeds/${feedId}/logs?limit=50`
        ),
      ]);

      const detailData =
        await detailResponse.json();

      const historyData =
        await historyResponse.json();

      const logsData =
        await logsResponse.json();

      if (!detailResponse.ok) {
        throw new Error(
          detailData.detail ||
            "Unable to load feed details."
        );
      }

      if (!historyResponse.ok) {
        throw new Error(
          historyData.detail ||
            "Unable to load feed history."
        );
      }

      if (!logsResponse.ok) {
        throw new Error(
          logsData.detail ||
            "Unable to load feed logs."
        );
      }

      setSelectedFeed(
        detailData.feed || null
      );

      setHistory(
        historyData.items || []
      );

      setLogs(
        logsData.items || []
      );
    } catch (detailsError) {
      console.error(
        "Threat feed details error:",
        detailsError
      );

      setError(
        detailsError.message ||
          "Unable to load feed details."
      );
    } finally {
      setActionKey("");
    }
  };

  const performFeedAction = async (
    feed,
    action
  ) => {
    const key = `${action}-${feed.id}`;

    setActionKey(key);
    setError("");

    try {
      const response = await apiFetch(`/threat-intel/feeds/${feed.id}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            actor: "George Taylor",
            role: "SOC Manager",
            initiated_by:
              "George Taylor",
            initiated_by_role:
              "SOC Manager",
            sync_type: "manual",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : data.detail?.error ||
              data.detail?.message ||
              `Unable to ${action} feed.`
        );
      }

      await loadFeeds(true);

      if (selectedFeed?.id === feed.id) {
        await openFeed(feed.id);
      }
    } catch (actionError) {
      console.error(
        `Threat feed ${action} error:`,
        actionError
      );

      setError(
        actionError.message ||
          `Unable to ${action} feed.`
      );
    } finally {
      setActionKey("");
    }
  };

  const totals = feeds.reduce(
    (current, feed) => {
      const health =
        getHealthStatus(feed);

      current.total += 1;

      if (feed.enabled) {
        current.enabled += 1;
      }

      if (health === "healthy") {
        current.healthy += 1;
      }

      if (health === "error") {
        current.errors += 1;
      }

      current.indicators += Number(
        feed.indicator_count || 0
      );

      return current;
    },
    {
      total: 0,
      enabled: 0,
      healthy: 0,
      errors: 0,
      indicators: 0,
    }
  );

  return (
    <div className="threatFeedsPage">
      <header className="threatFeedsHeader">
        <div>
          <p className="threatFeedsEyebrow">
            WirelessPress Threat Intelligence
          </p>

          <h1>
            Threat Feed Manager
          </h1>

          <p>
            Manage intelligence sources,
            monitor feed health, trigger
            synchronization, and review
            operational history.
          </p>
        </div>

        <div className="threatFeedsHeaderActions">
          <Link
            to="/threat-intelligence"
            className="threatFeedsSecondaryButton"
          >
            Dashboard
          </Link>

          <Link
            to="/threat-intelligence/indicators"
            className="threatFeedsSecondaryButton"
          >
            IOC Explorer
          </Link>

          <button
            type="button"
            className="threatFeedsPrimaryButton"
            onClick={() =>
              loadFeeds(true)
            }
            disabled={refreshing}
          >
            {refreshing
              ? "Refreshing..."
              : "Refresh Feeds"}
          </button>
        </div>
      </header>

      {error ? (
        <div className="threatFeedsError">
          {error}
        </div>
      ) : null}

      <section className="threatFeedsMetrics">
        <article>
          <span>
            Total Feeds
          </span>
          <strong>
            {formatNumber(totals.total)}
          </strong>
          <small>
            Configured sources
          </small>
        </article>

        <article>
          <span>
            Enabled
          </span>
          <strong>
            {formatNumber(totals.enabled)}
          </strong>
          <small>
            Active collections
          </small>
        </article>

        <article>
          <span>
            Healthy
          </span>
          <strong>
            {formatNumber(totals.healthy)}
          </strong>
          <small>
            Operational feeds
          </small>
        </article>

        <article>
          <span>
            Feed Errors
          </span>
          <strong>
            {formatNumber(totals.errors)}
          </strong>
          <small>
            Requires review
          </small>
        </article>

        <article>
          <span>
            Total Indicators
          </span>
          <strong>
            {formatNumber(
              totals.indicators
            )}
          </strong>
          <small>
            Across all feeds
          </small>
        </article>
      </section>

      <section className="threatFeedsPanel">
        <div className="threatFeedsPanelHeader">
          <div>
            <span>
              INTELLIGENCE SOURCES
            </span>

            <h2>
              Configured Threat Feeds
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="threatFeedsEmpty">
            Loading threat feeds...
          </div>
        ) : null}

        {!loading &&
        feeds.length === 0 ? (
          <div className="threatFeedsEmpty">
            No threat feeds configured.
          </div>
        ) : null}

        <div className="threatFeedsGrid">
          {feeds.map((feed) => {
            const health =
              getHealthStatus(feed);

            return (
              <article
                className="threatFeedCard"
                key={feed.id}
              >
                <div className="threatFeedCardHeader">
                  <div>
                    <strong>
                      {feed.name}
                    </strong>

                    <span>
                      {formatLabel(
                        feed.feed_type
                      )}
                    </span>
                  </div>

                  <span
                    className={`threatFeedStatus status-${health}`}
                  >
                    {formatLabel(health)}
                  </span>
                </div>

                <p>
                  {feed.description ||
                    "No description provided."}
                </p>

                <dl>
                  <div>
                    <dt>
                      Total IOCs
                    </dt>
                    <dd>
                      {formatNumber(
                        feed.indicator_count
                      )}
                    </dd>
                  </div>

                  <div>
                    <dt>
                      Active IOCs
                    </dt>
                    <dd>
                      {formatNumber(
                        feed.active_indicator_count
                      )}
                    </dd>
                  </div>

                  <div>
                    <dt>
                      Refresh
                    </dt>
                    <dd>
                      {
                        feed.refresh_interval_minutes
                      }{" "}
                      minutes
                    </dd>
                  </div>

                  <div>
                    <dt>
                      Last Success
                    </dt>
                    <dd>
                      {formatDate(
                        feed.last_success_at
                      )}
                    </dd>
                  </div>
                </dl>

                {feed.last_error ? (
                  <div className="threatFeedCardError">
                    {feed.last_error}
                  </div>
                ) : null}

                <div className="threatFeedCardActions">
                  <button
                    type="button"
                    onClick={() =>
                      openFeed(feed.id)
                    }
                    disabled={
                      actionKey ===
                      `details-${feed.id}`
                    }
                  >
                    {actionKey ===
                    `details-${feed.id}`
                      ? "Loading..."
                      : "View Details"}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      performFeedAction(
                        feed,
                        "sync"
                      )
                    }
                    disabled={
                      !feed.enabled ||
                      actionKey ===
                        `sync-${feed.id}`
                    }
                  >
                    {actionKey ===
                    `sync-${feed.id}`
                      ? "Syncing..."
                      : "Sync Now"}
                  </button>

                  <button
                    type="button"
                    className={
                      feed.enabled
                        ? "threatFeedDisableButton"
                        : "threatFeedEnableButton"
                    }
                    onClick={() =>
                      performFeedAction(
                        feed,
                        feed.enabled
                          ? "disable"
                          : "enable"
                      )
                    }
                    disabled={
                      actionKey ===
                      `${
                        feed.enabled
                          ? "disable"
                          : "enable"
                      }-${feed.id}`
                    }
                  >
                    {feed.enabled
                      ? "Disable"
                      : "Enable"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {selectedFeed ? (
        <div
          className="threatFeedsDrawerOverlay"
          onClick={() =>
            setSelectedFeed(null)
          }
        >
          <aside
            className="threatFeedsDrawer"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="threatFeedsDrawerHeader">
              <div>
                <span>
                  THREAT FEED DETAILS
                </span>

                <h2>
                  {selectedFeed.name}
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedFeed(null)
                }
              >
                ×
              </button>
            </div>

            <section className="threatFeedsDrawerSection">
              <h3>
                Configuration
              </h3>

              <dl>
                <div>
                  <dt>Feed Type</dt>
                  <dd>
                    {formatLabel(
                      selectedFeed.feed_type
                    )}
                  </dd>
                </div>

                <div>
                  <dt>Source URL</dt>
                  <dd>
                    {selectedFeed.source_url ||
                      "Not configured"}
                  </dd>
                </div>

                <div>
                  <dt>
                    Default Confidence
                  </dt>
                  <dd>
                    {
                      selectedFeed.confidence_default
                    }
                    %
                  </dd>
                </div>

                <div>
                  <dt>
                    Refresh Interval
                  </dt>
                  <dd>
                    {
                      selectedFeed.refresh_interval_minutes
                    }{" "}
                    minutes
                  </dd>
                </div>

                <div>
                  <dt>Status</dt>
                  <dd>
                    {selectedFeed.enabled
                      ? "Enabled"
                      : "Disabled"}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="threatFeedsDrawerSection">
              <h3>
                Synchronization History
              </h3>

              {history.length === 0 ? (
                <p>
                  No synchronization history.
                </p>
              ) : (
                <div className="threatFeedsHistory">
                  {history.map((item) => (
                    <article key={item.id}>
                      <div>
                        <strong>
                          {formatLabel(
                            item.status
                          )}
                        </strong>

                        <span>
                          {formatDate(
                            item.started_at
                          )}
                        </span>
                      </div>

                      <small>
                        Downloaded{" "}
                        {formatNumber(
                          item.indicators_downloaded
                        )}{" "}
                        · Created{" "}
                        {formatNumber(
                          item.indicators_created
                        )}{" "}
                        · Updated{" "}
                        {formatNumber(
                          item.indicators_updated
                        )}{" "}
                        · Errors{" "}
                        {formatNumber(
                          item.error_count
                        )}
                      </small>

                      {item.error_message ? (
                        <p>
                          {
                            item.error_message
                          }
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="threatFeedsDrawerSection">
              <h3>
                Audit Log
              </h3>

              {logs.length === 0 ? (
                <p>
                  No audit events.
                </p>
              ) : (
                <div className="threatFeedsLogs">
                  {logs.map((item) => (
                    <article key={item.id}>
                      <strong>
                        {formatLabel(
                          item.event_type
                        )}
                      </strong>

                      <p>
                        {item.message}
                      </p>

                      <small>
                        {formatDate(
                          item.created_at
                        )}{" "}
                        · {item.actor_name}
                      </small>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </aside>
        </div>
      ) : null}
    </div>
  );
}

export default ThreatFeedsPage;
